import React, { useContext, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client/react'
import PropTypes from 'prop-types'
import { useAlerts } from '@theneatcompany/nui'

import { INITIAL_LOAD } from '@/gql/common'
import { extractGraphQLErrorMessage } from '@/utils/errors'
import { trackRollbarPerson } from '@/utils/rollbar'
import tokens from '@/utils/tokens'

const AccountsContext = React.createContext({})

export const withAccounts = Comp => props => (
  <AccountsContext.Consumer>
    {providerProps => <Comp {...props} accounts={providerProps} />}
  </AccountsContext.Consumer>
)
export const useAccounts = () => useContext(AccountsContext)

export default function AccountsProvider({ children }) {
  const alerts = useAlerts()
  const { loading, data, error } = useQuery(INITIAL_LOAD, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (error) alerts.error(extractGraphQLErrorMessage(error))
  }, [error])

  const findPlanBySKU = (sku) => data?.plans?.find(p => p.sku === sku)

  const providerValue = useMemo(() => ({
    isLoading: loading,
    hasError: !!error,
    hasData: !!data,
    findPlanBySKU,
    ...data,
  }), [loading, data])

  useEffect(() => {
    if (!loading && data) {
      trackRollbarPerson(data)
      if (data.account?.id) {
        tokens.setTokenData({ accountId: data.account.id })
      }
    }
  }, [loading, data])

  return (
    <AccountsContext.Provider value={providerValue}>
      {children}
    </AccountsContext.Provider>
  )
}

AccountsProvider.propTypes = {
  children: PropTypes.element.isRequired,
}
